import { GenericBackendResponse } from '../genericBackendResponse';
import { validYearsToRetirement } from '../../utils/monthlyProjection';

/**
 * Qué rentabilidad se enseña de cada plan: la decide comparsa (`GET /plan-returns`, épica
 * product#14). Contrato en `comparsa/src/plan-returns/README.md`. Aquí no se elige periodo,
 * ni se suaviza, ni hay tasas de respaldo: si falla o un plan no tiene datos, se oculta su
 * curva con un aviso.
 */

export type PlanReturnsScenario = 'TRANSFER' | 'NEW_PLAN' | 'ORIGIN_ONLY';
export type PlanReturnsBlend = 'SINGLE_FUND' | 'EQUAL_WEIGHTS' | 'LIFECYCLE_BY_AGE';

export interface PlanReturnsSourceI {
	planCodigoN: string;
	planName: string | null;
	fundCodigosF: string[];
	/** Rentabilidad de un plan de referencia (plan hermano), no del propio. */
	isReference: boolean;
	blend: PlanReturnsBlend;
	provider: string | null;
}

/** Un tramo de edad del ciclo de vida, con el mismo reparto acciones/bonos. */
export interface PlanReturnsLifecycleStageI {
	/** Años desde hoy: [fromYear, toYear). */
	fromYear: number;
	toYear: number;
	ageFrom: number;
	ageTo: number;
	/** % en el fondo de acciones. */
	equityPct: number;
	/** Tasa mensual del tramo, sin redondear: la que usa la curva. */
	monthlyReturnPct: number;
	annualReturnPct: number;
}

/** Ciclo de vida (comparsa#806): sus dos fondos y, si se mandó la edad, los tramos. */
export interface PlanReturnsLifecycleI {
	equityFundCodigoF: string;
	bondFundCodigoF: string;
	/** `null` sin `yearsToRetirement`: entonces la tasa es la mezcla 50/50. */
	schedule: PlanReturnsLifecycleStageI[] | null;
}

export interface PlanReturnsItemI {
	/** Slug del catálogo (`null` en el plan del usuario). */
	catalogName: string | null;
	planCodigoN: string;
	planName: string | null;
	/** Sin ningún dato: no se pinta su curva. */
	noData: boolean;
	/** Neta de comisiones, en %. Para mostrar. */
	annualReturnPct: number | null;
	/** Para la curva mes a mes, sin redondear. */
	monthlyReturnPct: number | null;
	periodYears: number | null;
	/** Fechas del dato (ISO): fin = fecha de corte, inicio = N años antes. */
	period: { start: string; end: string } | null;
	/** Con 1 o 3 años de histórico. */
	shortHistoryWarning: boolean;
	/** «Comisión de gestión», en %. */
	managementFeePct: number | null;
	source: PlanReturnsSourceI | null;
	/** Solo planes de ciclo de vida con dato. Con tramos, la tasa de arriba es la del de hoy. */
	lifecycle?: PlanReturnsLifecycleI | null;
}

export interface PlanReturnsI {
	scenario: PlanReturnsScenario;
	/** Todos los planes con el mismo periodo. */
	samePeriod: boolean;
	rulesVersion: string;
	arcaPlans: PlanReturnsItemI[];
	originPlan: PlanReturnsItemI | null;
}

export interface PlanReturnsQuery {
	/** Slugs de `pension_plan_catalog.name` (`ArcaPlan.catalogSlug`). */
	arcaPlans?: string[];
	/** Código DGSFP (`N…`) del plan del usuario. */
	originDgsfp?: string;
	/** Fondo (`F…`) del plan del usuario, si se sabe. */
	originFund?: string;
	/** Años hasta la jubilación (1–49): los planes de ciclo de vida llegan con sus tramos. */
	yearsToRetirement?: number;
}

/** Cabeceras de lectura con la sesión del usuario. Nunca `x-password`: iría en el bundle público. */
const cabecerasConSesion = (token: string) => ({
	'Content-Type': 'application/json',
	Authorization: `Bearer ${token}`
});

async function fetchPlanReturns(token: string, path: string): Promise<PlanReturnsI | null> {
	try {
		const response = await fetch(`${process.env.REACT_APP_BASE_API}${path}`, {
			method: 'GET',
			headers: cabecerasConSesion(token)
		});
		if (!response.ok) return null;
		const body: GenericBackendResponse<PlanReturnsI> = await response.json();
		return body.success ? body.data : null;
	} catch {
		return null;
	}
}

/** Rentabilidad de los planes de Arca pedidos y/o del plan del usuario. `null` si falla. */
export async function getPlanReturns(
	token: string,
	query: PlanReturnsQuery
): Promise<PlanReturnsI | null> {
	const params = new URLSearchParams();
	if (query.arcaPlans?.length) params.set('arcaPlans', query.arcaPlans.join(','));
	if (query.originDgsfp) params.set('originDgsfp', query.originDgsfp);
	if (query.originFund) params.set('originFund', query.originFund);
	if (!token || params.toString() === '') return null;
	const years = validYearsToRetirement(query.yearsToRetirement);
	if (years) params.set('yearsToRetirement', String(years));
	return fetchPlanReturns(token, `/plan-returns?${params.toString()}`);
}

/**
 * Todos los planes de Arca activos, como plan nuevo (5 años). Con los años hasta la jubilación,
 * los de ciclo de vida llegan con sus tramos. `null` si falla.
 */
export async function getArcaPlanReturns(
	token: string,
	yearsToRetirement?: number
): Promise<PlanReturnsI | null> {
	if (!token) return null;
	const years = validYearsToRetirement(yearsToRetirement);
	const query = years ? `?yearsToRetirement=${years}` : '';
	return fetchPlanReturns(token, `/plan-returns/arca-plans${query}`);
}
